import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import Pagination from "../../components/ui/Pagination";
import {
  useAssignments,
  useAssignmentCount,
} from "../../api/queries/assetQueries";
import { getApiErrorMessage } from "../../utils/apiError";
import { formatDateTime } from "../../utils/dateTime";
import { AssignmentStatusBadge } from "./components/AssetBadges";
import BranchSelect from "../../components/ui/BranchSelect";
import { useAssetActions } from "./components/AssetActionDialogs";
import { employeeName } from "./components/assetErrors";
import { ExpectedReturn, AcknowledgementText } from "./components/AssetDisplay";
import "./assets.css";

// Each tab = a fixed set of API filters
const TABS = {
  active: { label: "Active", filters: { status: "ACTIVE" } },
  pending: {
    label: "Pending acknowledgement",
    filters: { pendingAcknowledgement: true },
  },
  overdue: { label: "Overdue", filters: { overdue: true } },
  history: { label: "History", filters: {} }, // RETURNED or LOST (picked below)
};

const TabCount = ({ filters }) => {
  const { data } = useAssignmentCount(filters);
  return <span className="approval-tab-count">{data ?? "…"}</span>;
};

const HistoryCount = () => {
  const returned = useAssignmentCount({ status: "RETURNED" });
  const lost = useAssignmentCount({ status: "LOST" });
  const total = (returned.data ?? 0) + (lost.data ?? 0);
  return (
    <span className="approval-tab-count">
      {returned.isLoading || lost.isLoading ? "…" : total}
    </span>
  );
};

const AssetAssignmentsPage = () => {
  const [params, setParams] = useSearchParams();
  const tab = TABS[params.get("tab")] ? params.get("tab") : "active";
  const historyStatus =
    params.get("historyStatus") === "LOST" ? "LOST" : "RETURNED";
  const page = Number(params.get("page")) || 1;
  const limit = Number(params.get("limit")) || 20;
  const search = params.get("search") || "";
  const branchId = params.get("branchId") || "";
  const [searchInput, setSearchInput] = useState(search);
  const { openAction, dialogs } = useAssetActions();

  const update = (changes, { resetPage = true } = {}) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) =>
      v ? next.set(k, String(v)) : next.delete(k),
    );
    if (resetPage && !("page" in changes)) next.delete("page");
    setParams(next, { replace: true });
  };

  useEffect(() => {
    if (searchInput === search) return undefined;
    const t = setTimeout(() => update({ search: searchInput.trim() }), 300);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const filters = useMemo(
    () => ({
      ...TABS[tab].filters,
      ...(tab === "history" ? { status: historyStatus } : {}),
      search,
      branchId,
      page,
      limit,
    }),
    [tab, historyStatus, search, branchId, page, limit],
  );
  const query = useAssignments(filters);
  const rows = query.data?.data || [];
  const total = query.data?.total || 0;

  const act = (type, a) => openAction(type, { asset: a.asset, assignment: a });

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <h1 className="fw-bold text-dark m-0 page-title">
              Asset Assignments
            </h1>
            <p className="text-muted small mb-0">
              Who has which asset, unacknowledged handovers, and overdue loans.
            </p>
          </div>
          <Link
            to="/assets"
            className="btn btn-sm px-3 fw-semibold d-flex align-items-center profile-btn-white h-35px rounded-8"
          >
            <i className="bi bi-box-seam me-2"></i> Asset register
          </Link>
        </div>

        <div className="approval-tabs" role="tablist">
          {Object.entries(TABS).map(([key, t]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className={`approval-tab ${tab === key ? "active" : ""}`}
              onClick={() =>
                update({ tab: key === "active" ? "" : key, historyStatus: "" })
              }
            >
              {t.label}{" "}
              {key === "history" ? (
                <HistoryCount />
              ) : (
                <TabCount filters={t.filters} />
              )}
            </button>
          ))}
        </div>

        <div className="asset-toolbar mb-3">
          <div className="position-relative asset-search">
            <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-slate-500 fs-080"></i>
            <input
              type="search"
              className="filter-input w-100 ps-5"
              placeholder="Search employee, employee number, asset tag, name or serial"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Search assignments"
            />
          </div>
          <div className="asset-branch-filter">
            <BranchSelect
              value={branchId}
              onChange={(v) => update({ branchId: v })}
              emptyLabel="All branches"
            />
          </div>
          {tab === "history" && (
            <div
              className="approval-subtabs"
              role="tablist"
              aria-label="History status"
            >
              {["RETURNED", "LOST"].map((s) => (
                <button
                  key={s}
                  type="button"
                  role="tab"
                  aria-selected={historyStatus === s}
                  className={`approval-subtab ${historyStatus === s ? "active" : ""}`}
                  onClick={() =>
                    update({ historyStatus: s === "RETURNED" ? "" : s })
                  }
                >
                  {s === "RETURNED" ? "Returned" : "Lost"}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="glass-table-card">
          <div className="table-responsive">
            <table className="table table-hover mb-0 asset-table">
              <thead className="custom-table-header">
                <tr>
                  <th className="ps-4 py-3">Employee</th>
                  <th className="py-3">Asset</th>
                  <th className="py-3">Branch</th>
                  <th className="py-3">Assigned</th>
                  <th className="py-3">Expected return</th>
                  <th className="py-3">Acknowledged</th>
                  <th className="py-3">Status</th>
                  <th className="py-3 pe-4 text-end">Actions</th>
                </tr>
              </thead>
              <tbody className="border-top-0">
                {rows.map((a) => (
                  <tr key={a.id}>
                    <td className="ps-4">
                      <div className="fw-semibold">
                        {employeeName(a.employee)}
                      </div>
                      <div className="text-slate-500 fs-075">
                        {a.employee?.employeeId}
                      </div>
                    </td>
                    <td>
                      <Link
                        to={`/assets/${a.asset?.id}`}
                        className="fw-bold text-navy text-decoration-none"
                      >
                        {a.asset?.assetTag}
                      </Link>
                      <div className="text-slate-500 fs-075">
                        {a.asset?.name}
                      </div>
                    </td>
                    <td>
                      {a.asset?.branch?.branchName || (
                        <span className="text-slate-500">Company level</span>
                      )}
                    </td>
                    <td className="text-nowrap fs-080">
                      {formatDateTime(a.assignedAt)}
                    </td>
                    <td className="text-nowrap fs-080">
                      {a.status === "ACTIVE" ? (
                        <ExpectedReturn date={a.expectedReturnDate} />
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="fs-080">
                      {a.status === "ACTIVE" ? (
                        <AcknowledgementText
                          acknowledgedAt={a.acknowledgedAt}
                        />
                      ) : a.acknowledgedAt ? (
                        "Yes"
                      ) : (
                        "No"
                      )}
                    </td>
                    <td>
                      <AssignmentStatusBadge status={a.status} />
                    </td>
                    <td className="pe-4 text-end text-nowrap">
                      {a.status === "ACTIVE" ? (
                        <div className="d-inline-flex gap-1">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary btn-icon-28"
                            title="Return"
                            aria-label="Return"
                            onClick={() => act("return", a)}
                          >
                            <i className="bi bi-box-arrow-in-left"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary btn-icon-28"
                            title="Transfer"
                            aria-label="Transfer"
                            onClick={() => act("transfer", a)}
                          >
                            <i className="bi bi-arrow-left-right"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger btn-icon-28"
                            title="Mark lost"
                            aria-label="Mark lost"
                            onClick={() => act("lost", a)}
                          >
                            <i className="bi bi-exclamation-octagon"></i>
                          </button>
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
                {!query.isLoading && !query.isError && rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-5 text-slate-500">
                      Nothing here.
                    </td>
                  </tr>
                )}
                {query.isError && (
                  <tr>
                    <td colSpan={8} className="text-center py-5 text-danger">
                      {getApiErrorMessage(
                        query.error,
                        "Could not load assignments.",
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {query.isLoading && (
            <div className="text-center py-5">
              <span
                className="spinner-border text-primary"
                role="status"
                aria-label="Loading"
              ></span>
            </div>
          )}
          <Pagination
            page={page}
            limit={limit}
            total={total}
            onPageChange={(p) => update({ page: p }, { resetPage: false })}
            onLimitChange={(l) => update({ limit: l })}
          />
        </div>
      </div>
      {dialogs}
    </Layout>
  );
};

export default AssetAssignmentsPage;
